'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { CustomImage } from 'components/shared/custom-image';
import v3 from 'components/landing/v3/v3.module.css';
import {
  bindOnceTextReveal,
  TEXT_REVEAL_FULL,
  TEXT_REVEAL_SUBTLE,
} from 'components/landing/v3/scrollBeat';
import { useTextReveal } from './hooks/useTextReveal';
import styles from './ClinicalAccuracy.module.css';

const ASSET = 'web_v2/why-ultrahuman/why-ultrahuman-clinical-cards';

/** A box on Figma's 464 x 560 card: x, y, width, height. */
type Box = [number, number, number, number];

type Feature = {
  /** i18n key under `clinicalAccuracy.features.` in the whyUltrahuman namespace */
  key: 'afib' | 'migraine' | 'cycleOvulation';
  /** File stem of `<file>-bg.jpg` (2x) and `<file>-content.png` (4x) */
  file: string;
  /** Figma's layer blur on the photo, in px; every photo also takes a 20%
      black tint */
  blur?: number;
  /** Where the app UI sits on the card */
  content: Box;
  /** Corner radius of the glass panel behind the UI; none on migraine */
  glass?: number;
  /** Partner logo under "Powered by", white on transparent, at Figma's width */
  partner: { name: string; src: string; width: number; height: number };
};

const FEATURES: Feature[] = [
  {
    key: 'afib',
    file: 'afib',
    blur: 17.5,
    content: [78, 140, 309, 236],
    glass: 14.73,
    partner: {
      name: 'FibriCheck',
      src: `${ASSET}/fibricheck-logo.png`,
      width: 114,
      height: 23,
    },
  },
  {
    key: 'migraine',
    file: 'migraine',
    content: [79, 118.09, 313, 280],
    partner: {
      name: 'Click Therapeutics',
      src: `${ASSET}/click-therapeutics-logo.png`,
      width: 142,
      height: 41,
    },
  },
  {
    key: 'cycleOvulation',
    file: 'cycle',
    content: [61, 141, 343, 234],
    glass: 12,
    partner: {
      name: 'OvuSense',
      src: 'web_v2/womens-health/vio/ovusense-logo-1.png',
      width: 124,
      height: 23,
    },
  },
];

const CARD = { w: 464, h: 560 } as const;

/* Box in percent of the card, so the layers scale with it. */
const boxStyle = ([x, y, w, h]: Box) => ({
  left: `${(x / CARD.w) * 100}%`,
  top: `${(y / CARD.h) * 100}%`,
  width: `${(w / CARD.w) * 100}%`,
  height: `${(h / CARD.h) * 100}%`,
});

/* Layers that build on each card as it scrolls in, after the sharp photo:
   the tint (and blur), the glass panel, the app UI, then "Powered by". */
/* Seconds between neighbouring cards in a row, as the hero cards. */
const CARD_STAGGER = 0.12;

const LAYER_DELAYS = {
  tint: 0.15,
  glass: 0.45,
  content: 0.7,
  poweredBy: 0.95,
} as const;

export const ClinicalAccuracy = () => {
  const t = useTranslations('whyUltrahuman');
  const sectionRef = useRef<HTMLElement>(null);
  const plateRefs = useRef<Array<HTMLDivElement | null>>([]);
  const gsapCtx = useGsapContext(sectionRef);
  useTextReveal(sectionRef);

  /* The cards fade up one by one across the row, as the hero cards do; on
     each, the tint, glass panel, app UI and "Powered by" follow a beat apart.
     Stacked on phones, each card simply reveals as it arrives. */
  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const inRow = window.matchMedia('(min-width: 992px)').matches;
      const cleanups = plateRefs.current.flatMap((plate, i) => {
        const card = plate?.parentElement;
        if (!plate || !card) return [];
        const offset = inRow ? i * CARD_STAGGER : 0;
        return [
          bindOnceTextReveal(card, {
            ...TEXT_REVEAL_FULL,
            delay: offset,
            trigger: card,
          }),
          ...(Object.keys(LAYER_DELAYS) as (keyof typeof LAYER_DELAYS)[]).map(
            (layer) =>
              bindOnceTextReveal(
                plate.querySelectorAll<HTMLElement>(
                  `[data-layer="${layer}"]`,
                )[0] ?? [],
                {
                  ...TEXT_REVEAL_SUBTLE,
                  y: layer === 'tint' ? 0 : TEXT_REVEAL_SUBTLE.y,
                  delay: offset + LAYER_DELAYS[layer],
                  trigger: card,
                },
              ),
          ),
        ];
      });
      return () => cleanups.forEach((cleanup) => cleanup());
    });
  }, [gsapCtx]);

  return (
    <section
      ref={sectionRef}
      id="clinical-accuracy"
      className={styles.section}
      aria-labelledby="why-ultrahuman-clinical-accuracy-title"
    >
      <div className={styles.intro}>
        <p className={`${v3.eyebrow} ${styles.eyebrow}`} data-reveal>
          {t('clinicalAccuracy.eyebrow')}
        </p>
        <h2
          id="why-ultrahuman-clinical-accuracy-title"
          className={v3.sectionTitle}
          data-reveal
        >
          {t('clinicalAccuracy.title')}
        </h2>
        <p className={styles.body} data-reveal>
          {t('clinicalAccuracy.body')}
        </p>
      </div>

      <ul className={styles.cards}>
        {FEATURES.map((feature, i) => (
          <li key={feature.key} className={styles.card}>
            <div
              ref={(node) => {
                plateRefs.current[i] = node;
              }}
              className={styles.plate}
            >
              <CustomImage
                className={styles.photo}
                src={`${ASSET}/${feature.file}-bg.jpg`}
                alt=""
                fill
                sizes="(min-width: 992px) 30vw, 100vw"
              />
              <span
                className={styles.tint}
                style={
                  feature.blur
                    ? {
                        WebkitBackdropFilter: `blur(${feature.blur}px)`,
                        backdropFilter: `blur(${feature.blur}px)`,
                      }
                    : undefined
                }
                data-layer="tint"
              />
              {feature.glass ? (
                <span
                  className={styles.glass}
                  style={{
                    ...boxStyle(feature.content),
                    borderRadius: feature.glass,
                  }}
                  data-layer="glass"
                />
              ) : null}
              <CustomImage
                className={styles.content}
                style={boxStyle(feature.content)}
                src={`${ASSET}/${feature.file}-content.png`}
                alt=""
                width={feature.content[2] * 2}
                height={feature.content[3] * 2}
                data-layer="content"
              />
              <div className={styles.poweredBy} data-layer="poweredBy">
                <p className={styles.poweredByLabel}>
                  {t('clinicalAccuracy.poweredBy')}
                </p>
                <CustomImage
                  className={styles.partnerLogo}
                  src={feature.partner.src}
                  alt={feature.partner.name}
                  width={feature.partner.width}
                  height={feature.partner.height}
                />
              </div>
            </div>
            <div className={styles.caption}>
              <h3 className={styles.cardTitle}>
                {t(`clinicalAccuracy.features.${feature.key}.title`)}
              </h3>
              <p className={styles.cardBody}>
                {t(`clinicalAccuracy.features.${feature.key}.body`)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
